import React, {useEffect, useState} from "react";
import {connect, useDispatch} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";
import * as lscache from "lscache";
import {bindActionCreators, Dispatch} from "redux";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {backgroundColor, mb, ph, pointer, w100} from "@pg-design/helpers-css";
import {BellIcon, ContactIcon} from "@pg-design/icons-module";
import {SystemModal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {FormActions} from "@pg-mono/form";
import {FormikForm, IFormikSubmitFn} from "@pg-mono/formik-utils";
import {postRequest} from "@pg-mono/request";
import {apiLink} from "@pg-mono/rp-api-routes";

import {APPLICATION_LOCAL_STORAGE_KEY} from "../../../app/constants/storage_keys";
import {IRPStore} from "../../../app/rp_reducer";
import {getPhoneDataFromString} from "../../../form/utils/get_phone_data_from_string";
import {getPhoneString} from "../../../form/utils/get_phone_string";
import {validationMessages} from "../../../form/utils/validation_messages";
import {addFailureNotification} from "../../../notifications/utils/add_failure_notification";
import {addSuccessNotification} from "../../../notifications/utils/add_success_notification";
import {fetchAllSearchLists, IFetchAllSearchListsParams} from "../../../search/actions/fetch_search_all_action";
import {searchFormFields} from "../../../search/constants/search_form_constants";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {SearchTab} from "../../../search/types/SearchTab";
import {gtmNotificationsSignup} from "../../../tracking/google_tag_manager/gtm_event_notifications_subscription";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {subscribeToOfferNotification} from "../../api/subsribe_to_offer_notification";
import {PhoneType} from "../../constants/offer_notification_chatbot_form";
import {OfferNotificationVariant} from "../../types/OfferNotificationVariant";
import {searchFormActions} from "../reducers/notifications_subscription_modal_reducer";
import {getPriceMessage} from "../utils/get_price_message";
import {getPropertyTypeMessage} from "../utils/get_property_type_message";
import {getRegionsMessage} from "../utils/get_regions_message";
import {getRoomsMessage} from "../utils/get_rooms_message";
import {OfferListNotificationsFiltersView} from "./OfferListFiltersView";
import {OfferListSummaryView, SummaryHeader} from "./OfferListSummaryView";

export enum NotificationVariant {
    BUTTON,
    ICON,
    MOBILE
}

export type SelectedFormValuesType = Pick<SearchFormValuesType, "type" | "search" | "rooms" | "price" | "distance">;

type NotificationValuesType = SelectedFormValuesType & {
    email: string;
    phone: {code: number; number: string};
    phone_marketing_consent: boolean;
    email_marketing_consent: boolean;
    general_marketing_consent: boolean;
};

interface IStateProps {
    formValues: SelectedFormValuesType;
    initialValues: SelectedFormValuesType;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
}

export interface INotificationsSubscriptionModalProps extends IStateProps, IActionsProps {
    variant?: NotificationVariant;
    customButton?: ({onClick}: {onClick: () => void}) => React.ReactNode;
}

const validationSchema = Yup.object().shape({
    email: Yup.string().required(validationMessages.required).email(validationMessages.email),
    phone: Yup.object({
        code: Yup.number().required(validationMessages.required),
        number: Yup.string().required(validationMessages.required)
    })
});

export const NotificationsSubscriptionModalC = (props: INotificationsSubscriptionModalProps) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const [isFiltersView, setIsFiltersView] = useState(false);
    const [temporaryFormValues, setTemporaryFormValues] = useState(props.formValues);
    const [isOpen, setIsOpen] = useState(false);
    const filters = props.formValues;
    const {data: userProfileData} = useGetUserProfileQuery();
    const lsUserInfo = lscache.get(APPLICATION_LOCAL_STORAGE_KEY);
    const {variant = NotificationVariant.BUTTON} = props;

    const initialValues = {
        email: userProfileData?.email || lsUserInfo?.email || "",
        phone: getPhoneDataFromString(userProfileData?.phone || lsUserInfo?.phone || ""),
        phone_marketing_consent: false,
        email_marketing_consent: false,
        general_marketing_consent: false,
        ...filters
    };

    const criteria = [
        getPropertyTypeMessage(filters.type),
        getRegionsMessage(filters.search.regions, filters.distance),
        getRoomsMessage(filters?.rooms),
        getPriceMessage(filters?.price?.upper, filters?.price?.lower)
    ]
        .filter((message) => !!message)
        .join(", ");

    const onSubmit: IFormikSubmitFn<NotificationValuesType> = (values) => {
        const {general_marketing_consent, phone_marketing_consent, email_marketing_consent, phone, email, ...restValues} = values;

        const data = {
            general_marketing_consent,
            phone_marketing_consent,
            email_marketing_consent,
            ...(phone.number && {phone: getPhoneString(phone)}),
            email,
            search_data: {
                ...restValues,
                ...filters,
                distance: filters.distance !== "" ? filters.distance : 5
            }
        };

        postRequest({}, apiLink.user.notifications({})(null), data)
            .then(() => {
                setIsOpen(false);
                gtmNotificationsSignup("sign_up");
                dispatch(addSuccessNotification("Pomyślnie zapisano na powiadomienia."));
                subscribeToOfferNotification(
                    {
                        ...values,
                        ...filters,
                        phone_type: PhoneType.None,
                        price: getPriceMessage(filters?.price?.upper, filters?.price?.lower),
                        price_lower_limit: parseInt(filters?.price?.lower),
                        price_upper_limit: parseInt(filters?.price?.upper)
                    },
                    OfferNotificationVariant.LIST,
                    userProfileData?.uuid
                );
            })
            .catch(() => {
                dispatch(addFailureNotification("Coś poszło nie tak. Spróbuj później."));
            });
    };

    useEffect(() => {
        if (isOpen) {
            props.formActions.update(props.initialValues);
            gtmNotificationsSignup("open");
        } else {
            setIsFiltersView(false);
        }
    }, [isOpen]);

    const renderButton = () => {
        if (props.customButton) {
            return props.customButton({onClick: () => setIsOpen(true)});
        }
        switch (variant) {
            case NotificationVariant.MOBILE:
                return <BellIcon size="1.8" onClick={() => setIsOpen(true)} />;
            case NotificationVariant.ICON:
                return <BellIcon size="2.4" wrapperSize="4.8" wrapperColor={theme.colors.primary} onClick={() => setIsOpen(true)} css={notificationsIcon} />;
            case NotificationVariant.BUTTON:
            default: {
                return (
                    <Button size="small" variant="filled_primary" iconLeft={ContactIcon} onClick={() => setIsOpen(true)} css={notificationsButton}>
                        Włącz powiadomienia
                    </Button>
                );
            }
        }
    };

    return (
        <>
            {renderButton()}

            <SystemModal
                isOpen={isOpen}
                variant="fit"
                onModalClose={() => setIsOpen(false)}
                css={(theme) => [w100, modalWidthStyle, isFiltersView && backgroundColor(theme.colors.gray[100])]}
            >
                <SystemModal.Header css={modalHeader} as="div">
                    {isFiltersView ? (
                        <Text as="h3" variant="headline_4" align="center" css={mb(4)}>
                            Kryteria wyszukiwania
                        </Text>
                    ) : (
                        <SummaryHeader />
                    )}
                </SystemModal.Header>
                <SystemModal.Content css={ph(3)}>
                    <FormikForm initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
                        {!isFiltersView && (
                            <OfferListSummaryView
                                criteria={criteria}
                                handleCriteriaButtonClick={() => {
                                    setIsFiltersView(true);
                                    setTemporaryFormValues(props.formValues);
                                }}
                            />
                        )}
                        {isFiltersView && (
                            <OfferListNotificationsFiltersView
                                {...props}
                                values={props.formValues}
                                onChange={props.formActions.update}
                                onNotificationsFiltersSubmit={(values) => {
                                    props.formActions.update(values);
                                    setIsFiltersView(false);
                                }}
                                onReset={() => {
                                    setIsFiltersView(false);
                                    props.formActions.update(temporaryFormValues);
                                }}
                            />
                        )}
                    </FormikForm>
                </SystemModal.Content>
            </SystemModal>
        </>
    );
};

export const OfferListNotificationsSubscriptionModal = connect(mapStateToProps, mapActionsToProps)(NotificationsSubscriptionModalC);
// Actions
function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    const searchContentParams = {
        googlePOIs: false,
        regions: true,
        offers: false,
        vendors: false
    };
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({fetchAllSearchLists: (searchInput: string) => fetchAllSearchLists(searchInput, {searchContent: searchContentParams})}, dispatch)
    };
}

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.notificationsSubscriptionModal.formValues as SelectedFormValuesType,
        initialValues: state.search.formValues as SelectedFormValuesType
    };
}
// Styles
const modalWidthStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        max-width: 640px;
    }
`;

const modalHeader = css`
    max-width: 592px;
    ${mb(0)};
    ${ph(3)};
`;

const notificationsButton = (theme: Theme) => css`
    ${mb(3)};
    width: 100%;

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(0)};
        max-width: 375px;
    }
`;

const notificationsIcon = css`
    ${pointer};
    &:hover {
        filter: saturate(0.75);
        ${elevation()};
    }
`;
import React, {useEffect, useState} from "react";
import {connect, useDispatch} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";
import * as lscache from "lscache";
import {bindActionCreators, Dispatch} from "redux";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {backgroundColor, mb, ph, pointer, w100} from "@pg-design/helpers-css";
import {BellIcon, ContactIcon} from "@pg-design/icons-module";
import {SystemModal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {FormActions} from "@pg-mono/form";
import {FormikForm, IFormikSubmitFn} from "@pg-mono/formik-utils";
import {postRequest} from "@pg-mono/request";
import {apiLink} from "@pg-mono/rp-api-routes";

import {APPLICATION_LOCAL_STORAGE_KEY} from "../../../app/constants/storage_keys";
import {IRPStore} from "../../../app/rp_reducer";
import {getPhoneDataFromString} from "../../../form/utils/get_phone_data_from_string";
import {getPhoneString} from "../../../form/utils/get_phone_string";
import {validationMessages} from "../../../form/utils/validation_messages";
import {addFailureNotification} from "../../../notifications/utils/add_failure_notification";
import {addSuccessNotification} from "../../../notifications/utils/add_success_notification";
import {fetchAllSearchLists, IFetchAllSearchListsParams} from "../../../search/actions/fetch_search_all_action";
import {searchFormFields} from "../../../search/constants/search_form_constants";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {SearchTab} from "../../../search/types/SearchTab";
import {gtmNotificationsSignup} from "../../../tracking/google_tag_manager/gtm_event_notifications_subscription";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {subscribeToOfferNotification} from "../../api/subsribe_to_offer_notification";
import {PhoneType} from "../../constants/offer_notification_chatbot_form";
import {OfferNotificationVariant} from "../../types/OfferNotificationVariant";
import {searchFormActions} from "../reducers/notifications_subscription_modal_reducer";
import {getPriceMessage} from "../utils/get_price_message";
import {getPropertyTypeMessage} from "../utils/get_property_type_message";
import {getRegionsMessage} from "../utils/get_regions_message";
import {getRoomsMessage} from "../utils/get_rooms_message";
import {OfferListNotificationsFiltersView} from "./OfferListFiltersView";
import {OfferListSummaryView, SummaryHeader} from "./OfferListSummaryView";

export enum NotificationVariant {
    BUTTON,
    ICON,
    MOBILE
}

export type SelectedFormValuesType = Pick<SearchFormValuesType, "type" | "search" | "rooms" | "price" | "distance">;

type NotificationValuesType = SelectedFormValuesType & {
    email: string;
    phone: {code: number; number: string};
    phone_marketing_consent: boolean;
    email_marketing_consent: boolean;
    general_marketing_consent: boolean;
};

interface IStateProps {
    formValues: SelectedFormValuesType;
    initialValues: SelectedFormValuesType;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
}

export interface INotificationsSubscriptionModalProps extends IStateProps, IActionsProps {
    variant?: NotificationVariant;
    customButton?: ({onClick}: {onClick: () => void}) => React.ReactNode;
}

const validationSchema = Yup.object().shape({
    email: Yup.string().required(validationMessages.required).email(validationMessages.email),
    phone: Yup.object({
        code: Yup.number().required(validationMessages.required),
        number: Yup.string().required(validationMessages.required)
    })
});

export const NotificationsSubscriptionModalC = (props: INotificationsSubscriptionModalProps) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const [isFiltersView, setIsFiltersView] = useState(false);
    const [temporaryFormValues, setTemporaryFormValues] = useState(props.formValues);
    const [isOpen, setIsOpen] = useState(false);
    const filters = props.formValues;
    const {data: userProfileData} = useGetUserProfileQuery();
    const lsUserInfo = lscache.get(APPLICATION_LOCAL_STORAGE_KEY);
    const {variant = NotificationVariant.BUTTON} = props;

    const initialValues = {
        email: userProfileData?.email || lsUserInfo?.email || "",
        phone: getPhoneDataFromString(userProfileData?.phone || lsUserInfo?.phone || ""),
        phone_marketing_consent: false,
        email_marketing_consent: false,
        general_marketing_consent: false,
        ...filters
    };

    const criteria = [
        getPropertyTypeMessage(filters.type),
        getRegionsMessage(filters.search.regions, filters.distance),
        getRoomsMessage(filters?.rooms),
        getPriceMessage(filters?.price?.upper, filters?.price?.lower)
    ]
        .filter((message) => !!message)
        .join(", ");

    const onSubmit: IFormikSubmitFn<NotificationValuesType> = (values) => {
        const {general_marketing_consent, phone_marketing_consent, email_marketing_consent, phone, email, ...restValues} = values;

        const data = {
            general_marketing_consent,
            phone_marketing_consent,
            email_marketing_consent,
            ...(phone.number && {phone: getPhoneString(phone)}),
            email,
            search_data: {
                ...restValues,
                ...filters,
                distance: filters.distance !== "" ? filters.distance : 5
            }
        };

        postRequest({}, apiLink.user.notifications({})(null), data)
            .then(() => {
                setIsOpen(false);
                gtmNotificationsSignup("sign_up");
                dispatch(addSuccessNotification("Pomyślnie zapisano na powiadomienia."));
                subscribeToOfferNotification(
                    {
                        ...values,
                        ...filters,
                        phone_type: PhoneType.None,
                        price: getPriceMessage(filters?.price?.upper, filters?.price?.lower),
                        price_lower_limit: parseInt(filters?.price?.lower),
                        price_upper_limit: parseInt(filters?.price?.upper)
                    },
                    OfferNotificationVariant.LIST,
                    userProfileData?.uuid
                );
            })
            .catch(() => {
                dispatch(addFailureNotification("Coś poszło nie tak. Spróbuj później."));
            });
    };

    useEffect(() => {
        if (isOpen) {
            props.formActions.update(props.initialValues);
            gtmNotificationsSignup("open");
        } else {
            setIsFiltersView(false);
        }
    }, [isOpen]);

    const renderButton = () => {
        if (props.customButton) {
            return props.customButton({onClick: () => setIsOpen(true)});
        }
        switch (variant) {
            case NotificationVariant.MOBILE:
                return <BellIcon size="1.8" onClick={() => setIsOpen(true)} />;
            case NotificationVariant.ICON:
                return <BellIcon size="2.4" wrapperSize="4.8" wrapperColor={theme.colors.primary} onClick={() => setIsOpen(true)} css={notificationsIcon} />;
            case NotificationVariant.BUTTON:
            default: {
                return (
                    <Button size="small" variant="filled_primary" iconLeft={ContactIcon} onClick={() => setIsOpen(true)} css={notificationsButton}>
                        Włącz powiadomienia
                    </Button>
                );
            }
        }
    };

    return (
        <>
            {renderButton()}

            <SystemModal
                isOpen={isOpen}
                variant="fit"
                onModalClose={() => setIsOpen(false)}
                css={(theme) => [w100, modalWidthStyle, isFiltersView && backgroundColor(theme.colors.gray[100])]}
            >
                <SystemModal.Header css={modalHeader} as="div">
                    {isFiltersView ? (
                        <Text as="h3" variant="headline_4" align="center" css={mb(4)}>
                            Kryteria wyszukiwania
                        </Text>
                    ) : (
                        <SummaryHeader />
                    )}
                </SystemModal.Header>
                <SystemModal.Content css={ph(3)}>
                    <FormikForm initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
                        {!isFiltersView && (
                            <OfferListSummaryView
                                criteria={criteria}
                                handleCriteriaButtonClick={() => {
                                    setIsFiltersView(true);
                                    setTemporaryFormValues(props.formValues);
                                }}
                            />
                        )}
                        {isFiltersView && (
                            <OfferListNotificationsFiltersView
                                {...props}
                                values={props.formValues}
                                onChange={props.formActions.update}
                                onNotificationsFiltersSubmit={(values) => {
                                    props.formActions.update(values);
                                    setIsFiltersView(false);
                                }}
                                onReset={() => {
                                    setIsFiltersView(false);
                                    props.formActions.update(temporaryFormValues);
                                }}
                            />
                        )}
                    </FormikForm>
                </SystemModal.Content>
            </SystemModal>
        </>
    );
};

export const OfferListNotificationsSubscriptionModal = connect(mapStateToProps, mapActionsToProps)(NotificationsSubscriptionModalC);
// Actions
function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    const searchContentParams = {
        googlePOIs: false,
        regions: true,
        offers: false,
        vendors: false
    };
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({fetchAllSearchLists: (searchInput: string) => fetchAllSearchLists(searchInput, {searchContent: searchContentParams})}, dispatch)
    };
}

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.notificationsSubscriptionModal.formValues as SelectedFormValuesType,
        initialValues: state.search.formValues as SelectedFormValuesType
    };
}
// Styles
const modalWidthStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        max-width: 640px;
    }
`;

const modalHeader = css`
    max-width: 592px;
    ${mb(0)};
    ${ph(3)};
`;

const notificationsButton = (theme: Theme) => css`
    ${mb(3)};
    width: 100%;

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(0)};
        max-width: 375px;
    }
`;

const notificationsIcon = css`
    ${pointer};
    &:hover {
        filter: saturate(0.75);
        ${elevation()};
    }
`;
